// Shared transport for the uploads. An object-store upload runs three
// steps: the page's server signs a PUT, the browser PUTs the file
// straight to the store, and the server commits what landed. A video
// upload's first request, which provisions the resumable upload, runs
// through postForm too. These helpers run the requests; each widget
// keeps its own steps, fields, and copy.
//
// A refusal throws an error whose userMessage is the sentence the server
// wrote for the person, when it wrote one. Show err.userMessage or the
// widget's own fallback, never err.message: that one is for the console.

// contentTypeOf is the type a file uploads as. A browser names no type
// for some files, and the store needs one to match the signature.
export function contentTypeOf(file) {
  return file.type || "application/octet-stream";
}

function signFields(file) {
  return {
    filename: file.name,
    content_type: contentTypeOf(file),
    file_size_bytes: String(file.size),
  };
}

// putFile PUTs file to a presigned URL. headers are the ones the sign
// step signed: the store refuses the signature unless they come back
// verbatim. The store is another origin, so a refused PUT can fail the
// fetch outright rather than answer a status; both throw.
export async function putFile(url, file, headers, { signal } = {}) {
  const res = await fetch(url, {
    method: "PUT",
    body: file,
    headers: { "Content-Type": contentTypeOf(file), ...headers },
    signal,
  });
  if (!res.ok) throw new Error(`upload: PUT answered ${res.status}`);
}

// signAndPut runs the first two steps: it asks signURL to sign a PUT for
// file, uploads the file, and answers what the sign step answered, for
// the widget's own commit.
export async function signAndPut(signURL, file, options = {}) {
  const signed = await postForm(signURL, signFields(file), options);
  if (!signed.upload_url) throw new Error(`upload: ${signURL} answered no URL`);

  await putFile(signed.upload_url, file, signed.upload_headers, options);

  return signed;
}

// request sends fields as a form and answers the response, or throws
// for a refusal. An ended session redirects to sign-in, which fetch
// follows: the page goes there, and the promise never settles, so the
// widget shows no error on its way out.
export async function request(
  url,
  { method = "POST", fields, accept = "application/json", signal } = {},
) {
  const res = await fetch(url, {
    method,
    body: fields ? new URLSearchParams(fields) : undefined,
    headers: { Accept: accept },
    credentials: "same-origin",
    signal,
  });
  if (!res.ok) throw await responseError(res);

  if (res.redirected) {
    window.location.assign(res.url);
    return new Promise(() => {});
  }

  return res;
}

// postForm is request for a JSON answer.
export async function postForm(url, fields, options = {}) {
  const res = await request(url, { ...options, fields });

  return res.json();
}

// responseError is the error for a refused response, carrying the
// sentence a JSON refusal wrote.
export async function responseError(res) {
  const err = new Error(`${res.url} answered ${res.status}`);

  try {
    const body = await res.json();
    if (body && typeof body.error === "string") err.userMessage = body.error;
  } catch (_) {
    // not JSON — the widget's fallback stands
  }

  return err;
}
